import { CSSProperties } from 'react';
import styled from 'styled-components';
import MarkdownBase, { Options } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  fontFamily,
  fontSize,
  fontWeight,
  lineHeight,
  radius,
  space,
} from '../../../style/utils';

const LIST_INDENT = '22px';
const H3_FONT_SIZE = '15px';

interface ChatMarkdownProps extends Options {
  dir?: 'auto' | 'ltr' | 'rtl';
  style?: CSSProperties;
}

const ChatMarkdownStyled = styled(MarkdownBase)`
  font-size: ${fontSize('base')};
  line-height: ${lineHeight('loose')};
  color: ${({ theme }) => theme.palette.content.primary};

  > *:first-child {
    margin-top: 0;
  }

  > *:last-child {
    margin-bottom: 0;
  }

  p {
    margin: 0;
    line-height: ${lineHeight('loose')};
  }

  p + p {
    margin-top: ${space('md')};
  }

  a {
    color: ${({ theme }) => theme.palette.accent.link};
  }

  ul,
  ol {
    margin: ${space('md', 0)};
    padding-left: ${LIST_INDENT};
    line-height: ${lineHeight('loose')};
  }

  li + li {
    margin-top: ${space('xs')};
  }

  li > ul,
  li > ol {
    margin: ${space('xs', 0)};
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: ${space('xl', 0, 'sm')};
    font-weight: ${({ theme }) => theme.typography.fontWeightBold};
    line-height: ${lineHeight('tight')};
  }

  h1 {
    font-size: ${fontSize('xl')};
  }

  h2 {
    font-size: ${fontSize('lg')};
  }

  h3 {
    font-size: ${H3_FONT_SIZE};
  }

  h4,
  h5,
  h6 {
    font-size: ${fontSize('base')};
  }

  blockquote {
    margin: ${space('md', 0)};
    padding-left: ${space('md')};
    border-left: 2px solid ${({ theme }) => theme.palette.border.default};
    color: ${({ theme }) => theme.palette.content.secondary};
  }

  code {
    font-family: ${fontFamily('mono')};
    font-weight: ${fontWeight('medium')};
    background: ${({ theme }) => theme.palette.surface.strong};
    color: ${({ theme }) => theme.palette.content.primary};
    padding: ${space(0, 'xs', 'hairline')};
    border-radius: ${radius('xxs')};
    white-space: pre-wrap;
  }
`;

const ChatMarkdown = (props: ChatMarkdownProps) => (
  <ChatMarkdownStyled {...props} remarkPlugins={[remarkGfm]} />
);

export default ChatMarkdown;
